import React, { useState } from 'react';
import PropTypes from 'prop-types';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import {
  Button, Typography, Dialog, DialogTitle,
  DialogActions, DialogContent, InputLabel,
  TextField, Grid, Checkbox, FormControlLabel,
} from '@mui/material';

import MultiSelect from '../../common/multi-select';
import { modalStyles } from '../../../lib/styles';

import {
  HIGH_PRIORITY,
  REGULAR_PRIORITY,
  shortDateTimeFormat2020,
  TaskTypes,
} from '../../../lib/utils';

function CreateEditTaskModal({
  open,
  handleCloseModal,
  handleSave,
  handleDelete,
  task,
  editing,
  pickupOptions,
  inspectOptions,
  vehicleOptions,
  wranglerOptions,
  bulk,
  lockVehicleField,
  disableDetailsNote,
  selectedCaseIds,
}) {
  const {
    note,
    priority,
    assignedVehicleIds = [],
    assignedToIds = [],
    pickupReason,
    inspectReason,
    automated,
    createdAt,
  } = task;
  const isPickupTask = task.task === TaskTypes.PICK_UP;

  const firstAssignedVehicleId = first(assignedVehicleIds);
  const firstAssignedWranglerId = first(assignedToIds);
  const [assignedVehicleIdValue, setAssignedVehicleId] = useState(firstAssignedVehicleId);
  const [assignedWranglerIdValue, setAssignedWranglerId] = useState(firstAssignedWranglerId);
  const [noteValue, setNote] = useState(note);
  const [priorityValue, setPriority] = useState(priority);
  const [reasonValue, setReason] = useState(isPickupTask ? pickupReason : inspectReason);

  if (open && selectedCaseIds && isEmpty(selectedCaseIds)) {
    return (
      <Dialog
        open={open}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="sm"
        disableEnforceFocus
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}>Please select a case</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      fullWidth
      PaperProps={{ sx: modalStyles.dialog }}
      disableEnforceFocus
    >
      <DialogTitle>
        <Grid>
          <Typography variant="h6" component="span">Task Details</Typography>
          {!bulk && (
            <Typography variant="subtitle2">
              {`${automated ? 'Automatically' : 'Manually'} requested on
              ${moment(createdAt).format(shortDateTimeFormat2020)}`}
            </Typography>
          )}
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Grid spacing={2} container alignItems="flex-start" direction="row">
          <Grid spacing={1} item container xs={12} md={6} direction="row">
            {!bulk && (
              <Grid item xs={12}>
                <Grid item sx={modalStyles.relative}>
                  <InputLabel sx={modalStyles.label}>Vehicle</InputLabel>
                  <MultiSelect
                    value={[assignedVehicleIdValue]}
                    options={vehicleOptions}
                    isSingle
                    isDisabled={editing || lockVehicleField}
                    placeholder="Select a vehicle (required)"
                    handleChange={(vehicleId) => setAssignedVehicleId(vehicleId)}
                  />
                </Grid>
              </Grid>
            )}
            <Grid item xs={12}>
              <Grid item sx={modalStyles.relative}>
                <InputLabel sx={modalStyles.label}>
                  {isPickupTask ? 'Pickup' : 'Inspect'}
                  &nbsp;Reason
                </InputLabel>
                <MultiSelect
                  value={[reasonValue]}
                  options={isPickupTask ? pickupOptions : inspectOptions}
                  isSingle
                  isDisabled={automated || !isPickupTask}
                  placeholder="Select a reason (required)"
                  handleChange={(reason) => setReason(reason)}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Grid item sx={modalStyles.relative}>
                <InputLabel sx={modalStyles.label}>Assigned To</InputLabel>
                <MultiSelect
                  value={[assignedWranglerIdValue]}
                  options={wranglerOptions}
                  isSingle
                  placeholder="Select an assigned user"
                  handleChange={(wranglerId) => setAssignedWranglerId(wranglerId)}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sx={{ mt: 2 }}>
              {disableDetailsNote ? (
                <Typography>{disableDetailsNote}</Typography>
              ) : (
                <>
                  <InputLabel>Details</InputLabel>
                  <TextField
                    margin="dense"
                    type="text"
                    multiline
                    variant="standard"
                    value={noteValue}
                    fullWidth
                    onChange={({ target: { value: newNote } }) => setNote(newNote)}
                  />
                </>
              )}
            </Grid>
          </Grid>
          <Grid item container xs={12} md={6}>
            <FormControlLabel
              value={priorityValue}
              key={priorityValue}
              control={(
                <Checkbox
                  value={priorityValue}
                  checked={priorityValue === HIGH_PRIORITY}
                  onClick={() => setPriority(priorityValue === HIGH_PRIORITY
                    ? REGULAR_PRIORITY
                    : HIGH_PRIORITY)}
                />
              )}
              label="Urgent Priority"
              labelPlacement="end"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container justifyContent="space-between">
          <Grid item>
            {!bulk && editing && (
              <Button
                onClick={() => handleDelete(true)}
                color="primary"
                variant="contained"
                disabled={automated}
              >
                Delete Task
              </Button>
            )}
          </Grid>
          <Grid item>
            <Button
              onClick={handleCloseModal}
              color="primary"
              variant="outlined"
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const updated = {
                  ...task,
                  note: noteValue,
                  priority: priorityValue,
                  assignedVehicleIds: [assignedVehicleIdValue],
                  assignedToIds: assignedWranglerIdValue ? [assignedWranglerIdValue] : [],
                  [isPickupTask ? 'pickupReason' : 'inspectReason']: reasonValue,
                };
                handleSave(updated);
              }}
              color="primary"
              variant="contained"
            >
              Submit
            </Button>
          </Grid>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

CreateEditTaskModal.defaultProps = {
  handleSave: () => {},
  task: {},
  bulk: false,
  editing: false,
  handleDelete: () => {},
  disableDetailsNote: undefined,
};
CreateEditTaskModal.propTypes = {
  handleCloseModal: PropTypes.func.isRequired,
  handleDelete: PropTypes.func,
  handleSave: PropTypes.func,
  open: PropTypes.bool.isRequired,
  bulk: PropTypes.bool,
  editing: PropTypes.bool,
  task: PropTypes.shape({
    _id: PropTypes.string,
    priority: PropTypes.string,
    createdAt: PropTypes.string,
    assignedVehicleIds: PropTypes.arrayOf(PropTypes.string),
  }),
  pickupOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool.isRequired,
  })).isRequired,
  inspectOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  vehicleOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  wranglerOptions: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  disableDetailsNote: PropTypes.string,
};

export default CreateEditTaskModal;
